import {
  cloneElement,
  forwardRef,
  useRef,
  type CSSProperties,
  type ReactElement,
  type Ref,
} from "react";
import { Transition, type TransitionStatus } from "react-transition-group";
import { useTheme } from "@mui/material/styles";
import type { TransitionProps } from "@mui/material/transitions";
import useForkRef from "@mui/utils/useForkRef";
import getReactElementRef from "@mui/utils/getReactElementRef";

const OFFSET = "10px";
const SHOWN: CSSProperties = { opacity: 1, transform: "translateY(0)" };

// Only the visible states are listed; exiting/exited fall through to the hidden
// base style in the render below (matches MUI's Grow/Fade pattern).
const styles: Partial<Record<TransitionStatus, CSSProperties>> = {
  entering: SHOWN,
  entered: SHOWN,
};

type SlideUpFadeProps = TransitionProps & { children: ReactElement };
type TransitionCb = (node: HTMLElement, isAppearing?: boolean) => void;

/** Forces a reflow so the enter transition starts from the hidden state. */
function reflow(node: HTMLElement): void {
  void node.scrollTop;
}

/**
 * Dialog transition: a quick translateY(10px) slide + fade on both enter and
 * exit. The transition is (re)applied imperatively in onEnter AND onExit so the
 * close animates even after the dialog has been open a while — mirrors MUI Grow.
 */
export const SlideUpFade = forwardRef(function SlideUpFade(
  props: SlideUpFadeProps,
  ref: Ref<unknown>
) {
  const {
    appear = true,
    children,
    in: inProp,
    onEnter,
    onEntered,
    onEntering,
    onExit,
    onExited,
    onExiting,
    style,
    timeout = 200,
    addEndListener,
    ...other
  } = props;
  const theme = useTheme();
  const nodeRef = useRef<HTMLElement>(null);
  const handleRef = useForkRef(nodeRef, getReactElementRef(children), ref);

  const applyTransition = (node: HTMLElement, mode: "enter" | "exit") => {
    const duration =
      typeof timeout === "number" ? timeout : (timeout[mode] ?? 200);
    node.style.transition = theme.transitions.create(["opacity", "transform"], {
      duration,
    });
  };

  const normalized = (cb?: TransitionCb) => (maybeIsAppearing?: boolean) => {
    const node = nodeRef.current;
    if (!cb || !node) return;
    if (maybeIsAppearing === undefined) cb(node);
    else cb(node, maybeIsAppearing);
  };

  const handleEnter = normalized((node, isAppearing) => {
    reflow(node); // start from the hidden state
    applyTransition(node, "enter");
    onEnter?.(node, isAppearing ?? false);
  });

  const handleExit = normalized((node) => {
    applyTransition(node, "exit");
    node.style.opacity = "0";
    node.style.transform = `translateY(${OFFSET})`;
    onExit?.(node);
  });

  return (
    <Transition
      appear={appear}
      in={inProp}
      nodeRef={nodeRef}
      timeout={timeout}
      onEnter={handleEnter}
      onEntering={normalized(onEntering as TransitionCb)}
      onEntered={normalized(onEntered as TransitionCb)}
      onExit={handleExit}
      onExiting={normalized(onExiting as TransitionCb)}
      onExited={normalized(onExited as TransitionCb)}
      addEndListener={
        addEndListener
          ? (next: () => void) =>
              addEndListener(nodeRef.current as HTMLElement, next)
          : undefined
      }
      {...other}
    >
      {(state) =>
        cloneElement(children, {
          style: {
            opacity: 0,
            transform: `translateY(${OFFSET})`,
            visibility: state === "exited" && !inProp ? "hidden" : undefined,
            ...styles[state],
            ...style,
            ...(children.props as { style?: CSSProperties }).style,
          },
          ref: handleRef,
        } as Partial<typeof children.props> & { ref: typeof handleRef })
      }
    </Transition>
  );
});
